import { motion, useReducedMotion } from "@/lib/motion";
import { scrollRevealViewport } from "@/lib/motion-viewport";
import { CHANGE_TYPE_CONFIG } from "./data";
import type { Entry, ChangeType } from "./types";

export function TypeBadge({ type }: { type: ChangeType }) {
  const cfg = CHANGE_TYPE_CONFIG[type];
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold"
      style={{ color: cfg.color, backgroundColor: cfg.bg, borderColor: cfg.border }}
    >
      {cfg.icon}
      {cfg.label}
    </span>
  );
}

export function ChangeBlock({
  change,
}: {
  change: { type: string; title: string; items: string[] };
}) {
  const type = change.type as ChangeType;
  const cfg = CHANGE_TYPE_CONFIG[type];

  return (
    <div className="py-6 first:pt-0 last:pb-0">
      <div className="mb-3 flex items-center gap-2">
        <span className="text-foreground text-sm font-semibold">{change.title}</span>
      </div>
      <ul className="space-y-2">
        {change.items.map((item) => (
          <li
            key={item}
            className="text-muted-foreground flex items-start gap-2.5 text-sm leading-relaxed"
          >
            <span
              className="mt-2 h-1 w-1 shrink-0 rounded-full"
              style={{ backgroundColor: cfg.color }}
              aria-hidden="true"
            />
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function TimelineEntry({ entry, index }: { entry: Entry; index: number }) {
  const prefersReducedMotion = useReducedMotion();
  const isSpecial = entry.isSpecial;
  const isInitial = entry.isInitial;

  return (
    <motion.div
      initial={prefersReducedMotion ? false : { opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={scrollRevealViewport}
      transition={{ duration: 0.5, delay: index * 0.04, ease: [0.22, 1, 0.36, 1] }}
      className="relative grid grid-cols-[120px_1fr] gap-6 sm:grid-cols-[160px_1fr] sm:gap-10"
    >
      {/* Left: date + connector */}
      <div className="relative flex flex-col items-end gap-1 pt-1">
        <div
          className="absolute top-3 right-0 bottom-0 w-px translate-x-[calc(50%+12px)]"
          style={{ backgroundColor: "var(--border)" }}
          aria-hidden="true"
        />
        <span className="text-muted-foreground text-xs font-medium whitespace-nowrap">
          {entry.date}
        </span>
      </div>

      {/* Connector dot */}
      <div
        className="border-background absolute top-[6px] left-[120px] z-10 h-3 w-3 flex-shrink-0 translate-x-[calc(-50%+20px)] rounded-full border-2 sm:left-[160px] sm:translate-x-[calc(-50%+22px)]"
        style={{
          backgroundColor: isSpecial
            ? "color-mix(in srgb, var(--foreground) 60%, transparent)"
            : isInitial
              ? "#5b8cff"
              : "var(--muted-foreground)",
          boxShadow: isSpecial
            ? "0 0 0 4px color-mix(in srgb, var(--foreground) 5%, transparent)"
            : undefined,
        }}
        aria-hidden="true"
      />

      {/* Right: card */}
      <div
        className="bg-card hover:border-brand/30 hover:bg-brand/[0.02] group relative mb-10 overflow-hidden rounded-xl border p-5 transition-all duration-200 sm:p-6"
        style={{
          borderColor: isSpecial
            ? "color-mix(in srgb, var(--foreground) 7%, transparent)"
            : "var(--border)",
          background: isSpecial
            ? "color-mix(in srgb, var(--foreground) 2%, transparent)"
            : undefined,
        }}
      >
        <div
          className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
          style={{
            background:
              "radial-gradient(ellipse 80% 60% at 50% 0%, color-mix(in srgb, var(--foreground) 5%, transparent), transparent 70%)",
          }}
          aria-hidden="true"
        />
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <h2 className="text-foreground text-base font-bold sm:text-lg">
            {entry.isSpecial || entry.isInitial ? entry.version : `v${entry.version}`}
          </h2>
          <div className="ml-auto flex flex-wrap items-center gap-1.5">
            {Array.from(new Set(entry.changes.map((c) => c.type as ChangeType))).map((t) => (
              <TypeBadge key={t} type={t} />
            ))}
          </div>
        </div>

        <div className="bg-border mb-4 h-px" aria-hidden="true" />

        <div className="divide-border/50 divide-y">
          {entry.changes.map((change) => (
            <ChangeBlock key={change.title} change={change} />
          ))}
        </div>
      </div>
    </motion.div>
  );
}
